import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db, doc, getDoc } from '../firebase/config';
import {
  Check, Package, MapPin, CreditCard, ShoppingBag,
  ChevronRight, AlertCircle, Truck, Home, Store, User
} from 'lucide-react';

const OrderConfirmationPage = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();   // may be a comma-separated list
  const { user } = useAuth();

  const [orders, setOrders] = useState([]);   // array of orders
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ---------- Load orders (single or multi) ----------
  useEffect(() => {
    if (!orderId) {
      setError('No order ID provided');
      setLoading(false);
      return;
    }

    const loadOrders = async () => {
      try {
        const ids = orderId.split(',').map(s => s.trim()).filter(Boolean);
        if (ids.length === 0) {
          setError('No order ID provided');
          setLoading(false);
          return;
        }

        const fetches = ids.map(async (id) => {
          const snap = await getDoc(doc(db, 'orders', id));
          if (!snap.exists()) return null;
          return { id: snap.id, ...snap.data() };
        });

        const results = (await Promise.all(fetches)).filter(Boolean);

        if (results.length === 0) {
          setError('Order not found');
          setLoading(false);
          return;
        }

        // Ownership check — every returned order must belong to this user
        const unauthorized = results.some(o => o.clientId && user?.uid && o.clientId !== user.uid);
        if (unauthorized) {
          setError('You do not have access to one or more of these orders');
          setLoading(false);
          return;
        }

        // Sort oldest first so they appear in checkout order
        results.sort((a, b) => {
          const aT = a.createdAt?.toMillis?.() ?? new Date(a.createdAt || 0).getTime();
          const bT = b.createdAt?.toMillis?.() ?? new Date(b.createdAt || 0).getTime();
          return aT - bT;
        });

        setOrders(results);
      } catch (err) {
        console.error('Error loading order(s):', err);
        setError('Failed to load order');
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, [orderId, user?.uid]);

  // ---------- Helpers ----------
  const formatPrice = (n) => `R${Number(n || 0).toFixed(2)}`;

  const formatDate = (ts) => {
    if (!ts) return '—';
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleDateString('en-ZA', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  const getItemImage = (item) => {
    if (item.image) return item.image;
    if (Array.isArray(item.media) && item.media.length > 0) {
      const first = item.media[0];
      if (first.type === 'image') return first.url;
    }
    if (item.mediaLegacy?.url) return item.mediaLegacy.url;
    return null;
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case 'paid':
        return { bg: 'bg-green-50', text: 'text-green-600', label: 'Payment received' };
      case 'processing':
        return { bg: 'bg-amber-50', text: 'text-amber-600', label: 'Processing' };
      case 'shipped':
        return { bg: 'bg-blue-50', text: 'text-blue-600', label: 'Shipped' };
      case 'delivered':
        return { bg: 'bg-green-50', text: 'text-green-600', label: 'Delivered' };
      case 'cancelled':
        return { bg: 'bg-red-50', text: 'text-red-600', label: 'Cancelled' };
      case 'pending':
        return { bg: 'bg-amber-50', text: 'text-amber-600', label: 'Pending payment' };
      default:
        return { bg: 'bg-gray-50', text: 'text-gray-600', label: status || 'Unknown' };
    }
  };

  const getDeliveryLabel = (order) => {
    if (order.deliveryMethod === 'standard') return `Standard · ${order.deliveryEstimate || '3-5 business days'}`;
    if (order.deliveryMethod === 'express') return `Express · ${order.deliveryEstimate || '1-2 business days'}`;
    if (order.deliveryMethod === 'collect') return 'Collect in person';
    return order.deliveryEstimate || 'Delivery';
  };

  // ---------- Loading ----------
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-[#EFA9B6] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // ---------- Error ----------
  if (error || orders.length === 0) {
    return (
      <div className="max-w-md mx-auto text-center py-20">
        <div className="w-24 h-24 bg-[#FFF5F6] rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertCircle size={40} className="text-[#EFA9B6]" />
        </div>
        <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2">
          {error || 'Order not found'}
        </h2>
        <p className="text-gray-400 mb-6 text-sm">
          {error === 'You do not have access to one or more of these orders'
            ? 'One or more orders belong to a different account.'
            : "We couldn't find that order. It may have been removed."}
        </p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={() => navigate('/dashboard/shop')}
            className="px-5 py-3 bg-[#FCF8F9] text-gray-700 rounded-full text-sm font-bold hover:bg-gray-100 transition-colors"
          >
            Back to Shop
          </button>
          <button
            onClick={() => navigate('/dashboard/orders')}
            className="px-5 py-3 bg-[#010101] text-white rounded-full text-sm font-bold hover:bg-black/90 transition-colors"
          >
            View My Orders
          </button>
        </div>
      </div>
    );
  }

  // ---------- Derived totals ----------
  const isMulti = orders.length > 1;
  const firstOrder = orders[0];
  const grandTotal = orders.reduce((sum, o) => sum + Number(o.total || 0), 0);
  const grandSubtotal = orders.reduce((sum, o) => sum + Number(o.subtotal || 0), 0);
  const grandDelivery = orders.reduce((sum, o) => sum + Number(o.deliveryFee ?? o.shipping ?? 0), 0);

  // Combined items count across all orders
  const totalItemCount = orders.reduce((sum, o) => sum + (o.items?.length || 0), 0);

  return (
    <div className="max-w-3xl mx-auto pb-20">

      {/* ============================================ */}
      {/* SUCCESS HEADER */}
      {/* ============================================ */}
      <div className="text-center mb-8 pt-6">
        <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <div className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center">
            <Check size={28} className="text-white" strokeWidth={3} />
          </div>
        </div>
        <h1 className="text-3xl font-serif font-bold text-gray-900 mb-2">
          {isMulti ? `${orders.length} Orders Confirmed` : 'Order Confirmed'}
        </h1>
        <p className="text-gray-500 text-sm max-w-md mx-auto">
          Thank you{firstOrder.clientName ? `, ${firstOrder.clientName.split(' ')[0]}` : ''}!
          {isMulti
            ? ` Your ${orders.length} orders from ${orders.length} vendors are on their way.`
            : ' Your order has been received and is being processed.'}
        </p>
      </div>

      {/* ============================================ */}
      {/* GRAND TOTAL META (multi-vendor only) */}
      {/* ============================================ */}
      {isMulti && (
        <div className="bg-white rounded-3xl border border-gray-100 p-5 mb-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em] mb-1">
                Total Paid
              </p>
              <p className="text-2xl font-serif font-bold text-gray-900">
                {formatPrice(grandTotal)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em] mb-1">
                Placed On
              </p>
              <p className="text-sm text-gray-700 font-medium">
                {formatDate(firstOrder.createdAt)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* PER-ORDER CARDS */}
      {/* ============================================ */}
      <div className="space-y-4 mb-6">
        {orders.map((order) => {
          const statusStyle = getStatusStyles(order.status);
          const items = order.items || [];
          const address = order.deliveryAddress || order.shippingAddress || {};
          const isCollect = order.deliveryMethod === 'collect';

          return (
            <div key={order.id} className="bg-white rounded-3xl border border-gray-100 overflow-hidden">

              {/* Order header */}
              <div className="p-5 border-b border-gray-50">
                <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-[#F4EEFF] flex items-center justify-center shrink-0 overflow-hidden">
                      {order.vendorImage ? (
                        <img src={order.vendorImage} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Store size={18} className="text-[#AF8BD4]" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em]">
                        Order from
                      </p>
                      <p className="font-bold text-gray-900 truncate">
                        {order.vendorName || 'Skytone'}
                      </p>
                    </div>
                  </div>
                  <div className={`px-3 py-1.5 rounded-full text-[10px] font-bold shrink-0 ${statusStyle.bg} ${statusStyle.text}`}>
                    {statusStyle.label}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-50">
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em] mb-0.5">
                      Order Number
                    </p>
                    <p className="font-mono font-bold text-gray-900 text-sm">
                      #{order.id.slice(-8).toUpperCase()}
                    </p>
                  </div>
                  {order.paymentReference && (
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em] mb-0.5">
                        Reference
                      </p>
                      <p className="text-xs text-gray-700 font-mono truncate">
                        {order.paymentReference}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Items */}
              <div className="p-5 border-b border-gray-50">
                <div className="flex items-center gap-2 mb-3">
                  <Package size={16} className="text-[#EFA9B6]" />
                  <h3 className="font-bold text-sm text-gray-900">
                    {items.length} item{items.length === 1 ? '' : 's'}
                  </h3>
                </div>

                <div className="space-y-3">
                  {items.map((item, idx) => {
                    const img = getItemImage(item);
                    return (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-[#FCF8F9] flex items-center justify-center overflow-hidden shrink-0">
                          {img ? (
                            <img src={img} alt={item.name} className="w-full h-full object-cover" />
                          ) : (
                            <ShoppingBag size={18} className="text-gray-300" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-sm text-gray-900 truncate">{item.name}</p>
                          {item.brand && (
                            <p className="text-xs text-gray-400 truncate">{item.brand}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-0.5">Qty: {item.quantity}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-bold text-sm text-gray-900">
                            {formatPrice((item.price || 0) * (item.quantity || 1))}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Delivery + totals */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5">
                {/* Delivery */}
                <div>
                  <h3 className="font-bold text-xs text-gray-900 mb-2 flex items-center gap-1.5">
                    {isCollect ? (
                      <Store size={14} className="text-[#EFA9B6]" />
                    ) : (
                      <Truck size={14} className="text-[#EFA9B6]" />
                    )}
                    {isCollect ? 'Collect from' : 'Deliver to'}
                  </h3>
                  <p className="text-[11px] text-[#AF8BD4] font-bold uppercase tracking-wider mb-1.5">
                    {getDeliveryLabel(order)}
                  </p>

                  {isCollect ? (
                    <div className="text-sm text-gray-700 leading-relaxed">
                      <p className="font-bold text-gray-900">{order.vendorName || 'Vendor'}</p>
                      <p className="text-gray-600">{order.collectAddress || 'Contact vendor for pickup'}</p>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-700 leading-relaxed">
                      <p className="font-bold text-gray-900">{address.fullName || '—'}</p>
                      {address.line1 && <p>{address.line1}</p>}
                      {address.address && <p>{address.address}</p>}
                      {(address.city || address.postalCode) && (
                        <p>
                          {address.city}
                          {address.postalCode ? `, ${address.postalCode}` : ''}
                        </p>
                      )}
                      {address.country && <p>{address.country}</p>}
                      {order.clientPhone && (
                        <p className="text-gray-500 mt-1 text-xs">{order.clientPhone}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Totals */}
                <div>
                  <h3 className="font-bold text-xs text-gray-900 mb-2 flex items-center gap-1.5">
                    <CreditCard size={14} className="text-[#EFA9B6]" />
                    Subtotal
                  </h3>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between text-gray-500">
                      <span>Items</span>
                      <span>{formatPrice(order.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-gray-500">
                      <span>Delivery</span>
                      <span>
                        {Number(order.deliveryFee ?? order.shipping ?? 0) === 0 ? (
                          <span className="text-green-600 font-medium">Free</span>
                        ) : (
                          formatPrice(order.deliveryFee ?? order.shipping)
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between font-bold text-gray-900 pt-1.5 border-t border-gray-50">
                      <span>Total</span>
                      <span>{formatPrice(order.total)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ============================================ */}
      {/* GRAND TOTAL SUMMARY (multi-vendor only) */}
      {/* ============================================ */}
      {isMulti && (
        <div className="bg-white rounded-3xl border border-gray-100 p-5 mb-4">
          <h3 className="font-serif font-bold text-gray-900 mb-3">All Orders Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-gray-500">
              <span>Subtotal ({totalItemCount} items)</span>
              <span>{formatPrice(grandSubtotal)}</span>
            </div>
            <div className="flex justify-between text-gray-500">
              <span>Total delivery</span>
              <span>
                {grandDelivery === 0 ? (
                  <span className="text-green-600 font-medium">Free</span>
                ) : (
                  formatPrice(grandDelivery)
                )}
              </span>
            </div>
            <div className="flex justify-between font-bold text-gray-900 pt-2 border-t border-gray-50 text-lg">
              <span>Total Paid</span>
              <span>{formatPrice(grandTotal)}</span>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* WHAT HAPPENS NEXT */}
      {/* ============================================ */}
      <div className="bg-[#FFF5F6] rounded-3xl p-5 mb-6">
        <h3 className="font-bold text-gray-900 text-sm mb-3 flex items-center gap-2">
          <Truck size={16} className="text-[#EFA9B6]" />
          What happens next?
        </h3>
        <ol className="space-y-2.5 text-sm text-gray-600">
          <li className="flex gap-3">
            <span className="w-5 h-5 rounded-full bg-white flex items-center justify-center text-[10px] font-bold text-[#EFA9B6] shrink-0">1</span>
            <span>
              {isMulti
                ? 'Each vendor confirms and prepares your items independently.'
                : 'The seller confirms your order and prepares it for shipping.'}
            </span>
          </li>
          <li className="flex gap-3">
            <span className="w-5 h-5 rounded-full bg-white flex items-center justify-center text-[10px] font-bold text-[#EFA9B6] shrink-0">2</span>
            <span>You'll get an update when your package ships or is ready for collection.</span>
          </li>
          <li className="flex gap-3">
            <span className="w-5 h-5 rounded-full bg-white flex items-center justify-center text-[10px] font-bold text-[#EFA9B6] shrink-0">3</span>
            <span>Track everything from <span className="font-bold text-gray-900">Your Orders</span> in your profile.</span>
          </li>
        </ol>
      </div>

      {/* ============================================ */}
      {/* ACTIONS */}
      {/* ============================================ */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => navigate('/dashboard/shop')}
          className="flex-1 py-4 rounded-full bg-[#FCF8F9] text-gray-700 font-bold text-sm hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
        >
          <Home size={16} />
          Continue Shopping
        </button>
        <button
          onClick={() => navigate('/dashboard/orders')}
          className="flex-1 py-4 rounded-full bg-[#010101] text-white font-bold text-sm hover:bg-black/90 transition-colors flex items-center justify-center gap-2"
        >
          View My Orders
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Small print */}
      <p className="text-center text-[11px] text-gray-400 mt-6 leading-relaxed">
        Need help with {isMulti ? 'these orders' : 'this order'}? Contact support with your order number
        <span className="font-mono font-bold text-gray-600">
          {' '}#{firstOrder.id.slice(-8).toUpperCase()}
          {isMulti && ` (+${orders.length - 1} more)`}
        </span>
      </p>
    </div>
  );
};

export default OrderConfirmationPage;